/**
 * Higher or Lower API helpers. Endless round flow (separate from the daily /today + /guess puzzle API):
 * start a run, then submit higher/lower guesses. The challenger's stat value is never returned until the
 * guess is scored server-side, so the client renders the challenger with its value hidden.
 */

import type { useApiClient } from '../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../constants/api';
import { HILO_GAME_KEY, type HiloChoice } from '../constants/higherLower.constants';

type ApiClient = ReturnType<typeof useApiClient>;

export interface HiloPlayerCard {
  id: string;
  name: string;
  club: string | null;
  /** Career club history (great trivia + recognition aid). Empty when not yet backfilled. */
  careerClubs: string[];
  nationality: string | null;
  position: string | null;
  retired: boolean;
  initials: string;
  statKey: string;
  statLabel: string;
  statUnit: string;
  /** Honesty caption shown with the value ('' = none, e.g. a complete career total needs no caveat). */
  coverageNote: string;
  /** Present on the baseline (visible) card; absent on the hidden challenger card. */
  statValue?: number;
}

export interface HiloRound {
  statKey: string;
  streak: number;
  baseline: HiloPlayerCard;
  challenger: HiloPlayerCard;
  coinsLeftToday: number;
  capReached: boolean;
  bestRun: number;
}

export interface HiloGuessCorrect {
  correct: true;
  challengerValue: number;
  streak: number;
  coinsAwarded: number;
  coins?: number;
  coinsLeftToday: number;
  capReached: boolean;
  next: { baseline: HiloPlayerCard; challenger: HiloPlayerCard };
}

export interface HiloGuessOver {
  correct: false;
  challengerValue: number;
  streak: number;
  runOver: true;
  bestRun: number;
  newBest: boolean;
  coinsLeftToday: number;
}

export type HiloGuessResult = HiloGuessCorrect | HiloGuessOver;

export async function startHiloRun(apiClient: ApiClient): Promise<HiloRound> {
  const response = await apiClient<{ success: boolean; data: HiloRound; message?: string }>(
    API_ENDPOINTS.games.roundStart(HILO_GAME_KEY),
    { method: 'POST', data: {} },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not start a round');
  return payload.data;
}

export async function submitHiloGuess(apiClient: ApiClient, choice: HiloChoice): Promise<HiloGuessResult> {
  const response = await apiClient<{ success: boolean; data: HiloGuessResult; message?: string }>(
    API_ENDPOINTS.games.roundGuess(HILO_GAME_KEY),
    { method: 'POST', data: { choice } },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not submit guess');
  return payload.data;
}
